import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import Section, { SectionHeading } from '../components/PageSection';
import { Lead, AccreditationNote } from '../components/ui';

const SITE = 'https://qasc.it';

const Body = styled(Typography)(({ theme }) => ({
  fontSize: '15.5px',
  lineHeight: 1.85,
  color: '#48606d',
  marginBottom: '18px',
}));

const StatCard = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '30px 20px',
  borderRadius: '20px',
  backgroundColor: '#e9f1f6',
  border: '1px solid rgba(0,66,92,0.08)',
  height: '100%',
}));

const StatNumber = styled(Typography)(({ theme }) => ({
  fontFamily: '"Sora", sans-serif',
  fontSize: 'clamp(28px, 3vw, 38px)',
  fontWeight: 700,
  color: '#00425c',
  lineHeight: 1.1,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '13.5px',
  color: '#48606d',
  marginTop: '10px',
  lineHeight: 1.55,
}));

const LogoCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  border: '1px solid rgba(0,66,92,0.10)',
  borderRadius: '18px',
  padding: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '120px',
  transition: 'all 0.3s ease',
  '&:hover': { boxShadow: '0 12px 28px rgba(0,66,92,0.10)' },
}));

const stats = [
  { number: 'Anni ’90', label: 'la nascita di QAS Consulting' },
  { number: '2011', label: 'prima società accreditata RINA per la UNI ISO 29990 (oggi ISO 21001:2019)' },
  { number: '2015', label: 'certificazione UNI EN ISO 9001:2015' },
  { number: '2 sedi', label: 'Spinea (VE) e Celano (AQ)' },
];

// Partner logos, as on the original chi-siamo page.
const partners = [
  { src: '/assets/images/partners/aifos.png', alt: 'AiFOS — Associazione Italiana Formatori ed Operatori della Sicurezza sul Lavoro' },
  { src: '/assets/images/partners/isre.png', alt: 'ISRE' },
  { src: '/assets/images/partners/made-hse.png', alt: 'MADE HSE' },
  { src: '/assets/images/partners/unilavoro.png', alt: 'UNILAVORO P.M.I.' },
];

// Certification badges, as on the original chi-siamo page.
const certifications = [
  { src: '/assets/images/certifications/iso-9001-accredia.png', alt: 'Certificazione UNI EN ISO 9001 — Apave Certification Italia / Accredia' },
  { src: '/assets/images/certifications/iso-21001.png', alt: 'Certificazione ISO 21001 — Apave Certification' },
  { src: '/assets/images/certifications/iso-45001.png', alt: 'Certificazione UNI ISO 45001' },
];

function ChiSiamoPage() {
  return (
    <PageLayout
      seo={{
        title: 'Chi Siamo | QAS Consulting',
        description:
          'QAS Consulting nasce negli anni ’90 per affiancare le imprese a 360°. Centro di formazione accreditato AiFOS e UNILAVORO P.M.I., prima società accreditata RINA per la UNI ISO 29990 (oggi ISO 21001:2019) e certificata UNI EN ISO 9001:2015.',
        canonical: `${SITE}/chi-siamo`,
      }}
      hero={{
        kicker: 'Chi siamo',
        title: 'QAS Consulting',
        subtitle:
          'Una realtà di professionisti nata negli anni ’90 con una missione: affiancare le imprese a 360° grazie alle competenze di un gruppo riunito in un’unica squadra.',
      }}
    >
      {/* (1) La storia */}
      <Section tone="light">
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <SectionHeading
                align="left"
                kicker="La nostra storia"
                title="Dalla sicurezza ai sistemi di gestione"
                sx={{ mb: 3 }}
              />
              <Lead>
                QAS Consulting nasce negli anni ’90: la mission era affiancare le imprese a 360°
                grazie alle competenze di un gruppo di professionisti riuniti in un’unica squadra.
              </Lead>
              <Body>
                Dal settore della sicurezza ci siamo poi specializzati nei settori dei sistemi di
                gestione aziendali. Il successo è arrivato grazie ai risultati ottenuti e al
                naturale passaparola. Il valore aggiunto che vogliamo far crescere presso i nostri
                clienti è il fattore umano, chiave in ogni organizzazione per il miglioramento
                continuo.
              </Body>
              <Body>
                Proprio per l’importanza della crescita culturale e umana delle persone
                all’interno delle aziende, QAS Consulting ha strutturato la propria attività e le
                proprie competenze arrivando nel 2011 a essere la prima società accreditata con
                l’ente di certificazione RINA Services S.p.A. per la norma UNI ISO 29990, oggi ISO
                21001:2019, per i servizi di formazione non formale; è inoltre Centro di
                Formazione accreditato AiFOS — Associazione Italiana Formatori ed Operatori della
                Sicurezza sul Lavoro — e UNILAVORO P.M.I.
              </Body>
              <Body>
                Oggi QAS Consulting progetta la formazione secondo quanto prevede il D.Lgs. 81/08
                e gli Accordi Stato-Regioni: puntiamo a migliorare la conoscenza di tutte le
                figure professionali costruendo insieme i percorsi di crescita e sviluppo
                personale. Per garantire il livello di eccellenza dei nostri servizi, sia di
                consulenza che di formazione, nel 2015 abbiamo conseguito la certificazione UNI EN
                ISO 9001:2015, per migliorare ancora la soddisfazione dei clienti e avere un
                sistema organizzato per raggiungere i nostri obiettivi.
              </Body>
              <Typography
                component={RouterLink}
                to="/contatti"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  fontFamily: '"Sora", sans-serif',
                  fontWeight: 600,
                  fontSize: '15.5px',
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { color: '#6d8511' },
                }}
              >
                Contattaci
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {stats.map((s) => (
                  <Grid item xs={6} key={s.label}>
                    <StatCard>
                      <StatNumber>{s.number}</StatNumber>
                      <StatLabel>{s.label}</StatLabel>
                    </StatCard>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* (2) Partner */}
      <Section tone="mist">
        <Container maxWidth="lg">
          <SectionHeading kicker="Chi siamo" title="Partner" />
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {partners.map((p) => (
              <Grid item xs={6} sm={3} key={p.alt}>
                <LogoCard>
                  <Box
                    component="img"
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    sx={{ maxWidth: '100%', maxHeight: '76px', objectFit: 'contain' }}
                  />
                </LogoCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>

      {/* (3) Certificazioni */}
      <Section tone="light">
        <Container maxWidth="lg">
          <SectionHeading kicker="Chi siamo" title="Certificazioni" />
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {certifications.map((c) => (
              <Grid item xs={6} sm={4} md={3} key={c.alt}>
                <LogoCard>
                  <Box
                    component="img"
                    src={c.src}
                    alt={c.alt}
                    loading="lazy"
                    sx={{ maxWidth: '100%', maxHeight: '86px', objectFit: 'contain' }}
                  />
                </LogoCard>
              </Grid>
            ))}
          </Grid>
          <AccreditationNote />
        </Container>
      </Section>
    </PageLayout>
  );
}

export default ChiSiamoPage;
